// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductDetailsQuantityOrderedStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsQuantityOrdered";
import { IconWrapper } from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductDetailsSummary from "@plumberssupply/App/Components/ProductDetails/ProductDetailsSummary";
import ProductUnitOfMeasureSelect from "@plumberssupply/Overrides/Components/ProductUnitOfMeasureSelect";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProductContext;

export interface ProductDetailsHeaderContentStyles {
    container?: InjectableCss;
    detailsSection?: InjectableCss;
    actionsSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    qtyOrdered?: ProductDetailsQuantityOrderedStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    priceUomWrapper?: InjectableCss;
}

export const productDetailsHeaderContentStyles: ProductDetailsHeaderContentStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        `,
    },
    detailsSection: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    actionsSection: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            width: 223px;
            justify-content: space-between;
            align-items: center;

            > button {
                width: 117px;
                min-width: 117px;
            }
        `,
    },
    productPartNumbers: {
        container: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    productPrice: {
        wrapper: {
            css: css`
                margin-bottom: 17px;
                text-align: right;
                margin-right: 0;
            `,
        },
        price: {
            priceText: {
                size: 24,
                lineHeight: "32px",
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    lineHeight: "26px",
                    css: css`
                        margin-left: 8px;
                    `,
                },
            },
        },
    },
    productUnitOfMeasureSelectStyles: {
        css: css`
            &&&&& {
                height: 26px;
                font-size: 16px;
                line-height: 26px;
                padding-left: 8px;
            }
        `,
        cssOverrides: {
            formField: css`
                margin: 7px 0 0 10px;
                &&& {
                    &:before {
                        line-height: 26px;
                        height: 26px;
                    }

                    ${IconWrapper} {
                        &,
                        & svg {
                            width: 8px;
                            height: 26px;
                        }
                    }
                }
            `,
            inputSelect: css`
                overflow: hidden;
            `,
        },
    },
    priceUomWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
};

const styles = productDetailsHeaderContentStyles;

const ProductDetailsHeaderContent: FC<Props> = ({ productContext }) => {
    const { product } = productContext;
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.detailsSection}>
                <ProductDetailsSummary product={product} />
            </StyledWrapper>
            <StyledWrapper {...styles.actionsSection}>
                <StyledWrapper {...styles.priceUomWrapper}>
                    <ProductPrice
                        showLabel={false}
                        product={productContext}
                        extendedStyles={styles.productPrice}
                        showUnitOfMeasure={product && product.unitOfMeasures?.length === 1}
                    />
                    {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                        <ProductUnitOfMeasureSelect
                            labelOverride=""
                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                            id={`${product.id}-uom`}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.addToCartSection}>
                    {createWidgetElement("ProductDetails/ProductDetailsQuantityOrdered", {
                        fields: {},
                    })}
                    {createWidgetElement("ProductDetails/ProductDetailsAddToCartButton", {
                        fields: {},
                    })}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductDetailsHeaderContent),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
